import type { MediaGenProvider, MediaGenCapability, ShotMode } from "./types.js";

/**
 * Media-generation model registry.
 *
 * One entry per (provider, capability) we can generate with. `usageUnits` and
 * `addOn` are the levers for the cost model: premium models cost more units,
 * and flipping `addOn` to true bills that capability as a paid add-on instead
 * of counting against included plan usage — so making video an add-on before
 * go-live is a one-line config change here, not a rearchit: see the billing
 * metering in @notamen/core/billing.
 *
 * `usageUnits` is cost-grounded: ~1 unit ≈ $0.05 of real provider cost, so a
 * single overage price per unit fairly covers both photo and video (a video
 * clip simply costs more units). Values track approximate provider list price
 * plus light overhead — Nova Canvas image 1, Gemini image 1, Nova Reel ~6s
 * video 8, Veo 3 ~8s video 80. Tunable in one place; not pricing commitments.
 */
/**
 * Per-shot-mode routing for a Bedrock image model whose shot modes map to
 * DIFFERENT provider model ids and/or regions. Stability on Bedrock needs this:
 * scene (pure text→image) uses Stable Image Core, which is only offered in
 * us-west-2; composite/reference condition on the product image and use the
 * Stable Image editing models, which live in us-east-1 but must be invoked
 * through their `us.`-prefixed inference-profile id (the bare id rejects
 * on-demand throughput). One user-facing model, three real ids behind it.
 */
export interface ShotRoute {
  /** Provider/inference-profile model id used for the actual InvokeModel call. */
  providerModelId: string;
  /** Region to invoke in. Omitted = the client's default region (us-east-1). */
  region?: string;
}

export interface MediaModelDef {
  /** Logical id used across our app + API. */
  id: string;
  provider: MediaGenProvider;
  capability: MediaGenCapability;
  /** Human label for the model toggle in the UI. */
  label: string;
  /** Provider-native model id used in the actual API call. */
  providerModelId: string;
  /**
   * Bedrock "family" that decides the request/response wire format. Defaults to
   * the provider's primary family. `nova` = Amazon Nova request shape;
   * `stability` = Stability AI Stable Image request shape.
   */
  bedrockFamily?: "nova" | "stability";
  /**
   * Optional per-shot-mode override of the model id + region (see ShotRoute).
   * When a shot mode isn't listed here, the flat `providerModelId` + the
   * client's default region are used. Only Stable Image needs this today.
   */
  shotRouting?: Partial<Record<ShotMode, ShotRoute>>;
  /** Usage units consumed per generation, metered against the plan. */
  usageUnits: number;
  /**
   * When true, this capability is billed as a paid add-on rather than counting
   * against included plan usage. Flip video here (no code change) to make video
   * generation a paid add-on.
   */
  addOn: boolean;
  /** Rough quality/cost tier, surfaced in the UI. */
  tier: "standard" | "premium";
  /** Shot modes this model supports. */
  shotModes: ShotMode[];
}

/** Every shot mode (used as the default support set for image models). */
const ALL_SHOT_MODES: ShotMode[] = ["composite", "reference", "scene"];
/** Video supports a generated/real first frame (reference) or pure scene. */
const VIDEO_SHOT_MODES: ShotMode[] = ["reference", "scene"];

export const MEDIA_MODELS: MediaModelDef[] = [
  // ── AWS Bedrock — Stability AI Stable Image ─────────────────────────────
  // One user-facing image model. Its three shot modes route to three different
  // Stability model ids in two regions (see shotRouting): scene → Stable Image
  // Core (us-west-2, text→image); composite → Stable Image Control Structure
  // (us-east-1 inference profile, product structure-conditioned); reference →
  // Stable Image Style Guide (us-east-1 inference profile, product style-
  // conditioned). Replaces Amazon Nova Canvas, which AWS marked Legacy.
  {
    id: "stable-image",
    provider: "bedrock",
    capability: "image",
    label: "Stability AI",
    bedrockFamily: "stability",
    // Default id used only if a shot mode isn't in shotRouting (none today).
    providerModelId: "stability.stable-image-core-v1:1",
    shotRouting: {
      scene: { providerModelId: "stability.stable-image-core-v1:1", region: "us-west-2" },
      composite: { providerModelId: "us.stability.stable-image-control-structure-v1:0", region: "us-east-1" },
      reference: { providerModelId: "us.stability.stable-image-style-guide-v1:0", region: "us-east-1" },
    },
    usageUnits: 1,
    addOn: false,
    tier: "standard",
    shotModes: ALL_SHOT_MODES,
  },
  {
    id: "nova-reel",
    provider: "bedrock",
    capability: "video",
    label: "Amazon Nova Reel",
    providerModelId: "amazon.nova-reel-v1:1",
    usageUnits: 8,
    addOn: false,
    tier: "standard",
    shotModes: VIDEO_SHOT_MODES,
  },
  // ── Google Vertex (premium; configured later, see VertexConfig) ─────────
  {
    id: "gemini-image",
    provider: "vertex",
    capability: "image",
    label: "Google Gemini Image",
    providerModelId: "gemini-2.5-flash-image",
    usageUnits: 1,
    addOn: false,
    tier: "premium",
    shotModes: ALL_SHOT_MODES,
  },
  {
    id: "veo-3",
    provider: "vertex",
    capability: "video",
    label: "Google Veo 3",
    providerModelId: "veo-3.0-generate-001",
    usageUnits: 80,
    addOn: false,
    tier: "premium",
    shotModes: VIDEO_SHOT_MODES,
  },
];

export function getMediaModel(id: string): MediaModelDef | undefined {
  return MEDIA_MODELS.find((m) => m.id === id);
}

export function mediaModelsFor(capability: MediaGenCapability): MediaModelDef[] {
  return MEDIA_MODELS.filter((m) => m.capability === capability);
}

/** First model for a (provider, capability), used as the per-provider default. */
export function defaultMediaModel(
  provider: MediaGenProvider,
  capability: MediaGenCapability,
): MediaModelDef | undefined {
  return MEDIA_MODELS.find((m) => m.provider === provider && m.capability === capability);
}
